import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { PROVINSI, KOTA_ALL as KOTA, KECAMATAN_ALL as KECAMATAN, ALL_LOCATIONS, type Location } from "@/data/locations";
import { LOCATION_SOURCES, LAST_UPDATED } from "@/data/locations-sources";
import { getFavorites, getHistory, type PrayerLink } from "@/lib/prayer/favorites";
import { Search, Star, Clock, MapPin, ExternalLink, Globe } from "lucide-react";
import { cn } from "@/lib/utils";
import { displayLocationTitle } from "@/lib/prayer/copy";

type Variant = "sholat" | "imsak" | "buka-puasa";
type Tab = "semua" | "provinsi" | "kota" | "kecamatan" | "favorit";

const PULAU: Record<string, string[]> = {
  Sumatera: ["aceh", "sumatera-utara", "sumatera-barat", "riau", "kepulauan-riau", "jambi", "sumatera-selatan", "bangka-belitung", "bengkulu", "lampung"],
  Jawa: ["dki-jakarta", "jawa-barat", "banten", "jawa-tengah", "di-yogyakarta", "jawa-timur"],
  "Bali & Nusra": ["bali", "nusa-tenggara-barat", "nusa-tenggara-timur"],
  Kalimantan: ["kalimantan-barat", "kalimantan-tengah", "kalimantan-selatan", "kalimantan-timur", "kalimantan-utara"],
  Sulawesi: ["sulawesi-utara", "gorontalo", "sulawesi-tengah", "sulawesi-barat", "sulawesi-selatan", "sulawesi-tenggara"],
  Maluku: ["maluku", "maluku-utara"],
  Papua: ["papua-barat", "papua-barat-daya", "papua", "papua-tengah", "papua-pegunungan", "papua-selatan"],
};

function locUrl(variant: Variant, l: Location): string {
  const base = variant === "buka-puasa" ? "/jadwal-buka-puasa" : `/jadwal-${variant}`;
  if (l.kind === "provinsi") return `${base}/${l.slug}`;
  if (l.kind === "kota") return `${base}/${l.slug}`;
  return `${base}/${l.parentSlug}/${l.slug}`;
}

export function LocationIndex({ variant }: { variant: Variant }) {
  const [tab, setTab] = useState<Tab>("semua");
  const [q, setQ] = useState("");
  const [pulau, setPulau] = useState<string>("Semua");
  const [letter, setLetter] = useState<string | null>(null);
  const [favs, setFavs] = useState<PrayerLink[]>([]);
  const [hist, setHist] = useState<PrayerLink[]>([]);

  useEffect(() => {
    const refresh = () => { setFavs(getFavorites().filter((x) => x.variant === variant)); setHist(getHistory().filter((x) => x.variant === variant)); };
    refresh();
    window.addEventListener("prayer:store", refresh);
    return () => window.removeEventListener("prayer:store", refresh);
  }, [variant]);

  const provSlugFilter = pulau === "Semua" ? null : new Set(PULAU[pulau]);
  const matches = (l: Location) => {
    if (q && !l.name.toLowerCase().includes(q.toLowerCase())) return false;
    if (letter && l.name[0]?.toUpperCase() !== letter) return false;
    if (provSlugFilter && !provSlugFilter.has(l.provinceSlug)) return false;
    return true;
  };
  const provinsi = useMemo(() => PROVINSI.filter(matches), [q, pulau, letter]);
  const kota = useMemo(() => KOTA.filter(matches), [q, pulau, letter]);
  const kecamatan = useMemo(() => KECAMATAN.filter(matches), [q, pulau, letter]);
  const semua = useMemo(() => {
    // Combined cross-type results, sorted: query relevance first, then kind weight.
    const needle = q.trim().toLowerCase();
    const list = ALL_LOCATIONS.filter(matches);
    if (!needle) return list.slice(0, 200);
    const scored = list.map((l) => {
      const n = l.name.toLowerCase();
      let s = 0;
      if (n === needle) s = 100;
      else if (n.startsWith(needle)) s = 60;
      else if (n.includes(needle)) s = 30;
      if (l.kind === "kecamatan") s += 2;
      if (l.kind === "kota") s += 1;
      return { l, s };
    });
    scored.sort((a, b) => b.s - a.s);
    return scored.map((x) => x.l).slice(0, 300);
  }, [q, pulau, letter]);

  const titlePrefix =
    variant === "sholat" ? "Jadwal Sholat" :
    variant === "imsak" ? "Jadwal Imsakiyah" :
    "Jadwal Buka Puasa";
  const counts = {
    semua: PROVINSI.length + KOTA.length + KECAMATAN.length,
    provinsi: PROVINSI.length,
    kota: KOTA.length,
    kecamatan: KECAMATAN.length,
  };

  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const kotaByProv = useMemo(() => {
    const map = new Map<string, Location[]>();
    for (const k of kota) { const arr = map.get(k.parentSlug!) ?? []; arr.push(k); map.set(k.parentSlug!, arr); }
    return map;
  }, [kota]);
  const kecByKota = useMemo(() => {
    const map = new Map<string, Location[]>();
    for (const k of kecamatan) { const arr = map.get(k.parentSlug!) ?? []; arr.push(k); map.set(k.parentSlug!, arr); }
    return map;
  }, [kecamatan]);

  return (
    <SiteShell>
      <header className="rounded-xl bg-prayer/10 p-5 ring-1 ring-prayer/20">
        <h1 className="font-display text-3xl tracking-tight md:text-4xl">Indeks Lokasi {titlePrefix}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {counts.provinsi} provinsi · {counts.kota}+ kota/kabupaten · {counts.kecamatan}+ kecamatan di seluruh Indonesia. Diperbarui {LAST_UPDATED}.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <label className="relative flex-1 min-w-[200px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search" placeholder="Cari provinsi, kota, atau kecamatan..."
              value={q} onChange={(e) => setQ(e.target.value)}
              className="w-full rounded-md border border-border bg-card py-2 pl-9 pr-3 text-sm focus:border-prayer focus:outline-none focus:ring-1 focus:ring-prayer"
            />
          </label>
          <select value={pulau} onChange={(e) => setPulau(e.target.value)} className="rounded-md border border-border bg-card px-3 py-2 text-sm">
            <option>Semua</option>
            {Object.keys(PULAU).map((p) => <option key={p}>{p}</option>)}
          </select>
        </div>
      </header>

      {/* Favorites & History — always visible at top if any */}
      {(favs.length > 0 || hist.length > 0) && (
        <section className="mt-5 grid gap-3 md:grid-cols-2">
          {favs.length > 0 && (
            <div className="rounded-lg border border-border bg-card p-4">
              <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold"><Star className="h-4 w-4 fill-prayer text-prayer" /> Favorit Anda</h2>
              <div className="flex flex-wrap gap-1.5">
                {favs.slice(0, 10).map((f) => (
                  <Link key={f.href} to={f.href as never} className="rounded-md border border-prayer/40 bg-prayer/5 px-2.5 py-1 text-xs text-prayer hover:bg-prayer/10">{f.name}</Link>
                ))}
              </div>
            </div>
          )}
          {hist.length > 0 && (
            <div className="rounded-lg border border-border bg-card p-4">
              <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold"><Clock className="h-4 w-4 text-muted-foreground" /> Terakhir Dilihat</h2>
              <div className="flex flex-wrap gap-1.5">
                {hist.slice(0, 8).map((f) => (
                  <Link key={f.href} to={f.href as never} className="rounded-md border border-border bg-background px-2.5 py-1 text-xs hover:border-prayer hover:text-prayer">{f.name}</Link>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Tabs */}
      <div className="mt-5 inline-flex flex-wrap rounded-md border border-border bg-card p-0.5">
        {([
          ["semua", `Semua (${counts.semua})`],
          ["provinsi", `Provinsi (${counts.provinsi})`],
          ["kota", `Kota/Kab (${counts.kota})`],
          ["kecamatan", `Kecamatan (${counts.kecamatan})`],
          ["favorit", "Favorit"],
        ] as [Tab, string][]).map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)} className={cn("rounded px-3 py-1.5 text-sm", tab === k ? "bg-prayer text-prayer-foreground" : "hover:bg-accent")}>
            {label}
          </button>
        ))}
      </div>

      {/* A-Z jump bar */}
      {tab !== "favorit" && (
        <div className="mt-3 flex flex-wrap gap-1">
          <button type="button" onClick={() => setLetter(null)} className={cn("rounded px-2 py-0.5 text-xs", !letter ? "bg-prayer text-prayer-foreground" : "border border-border hover:bg-accent")}>Semua</button>
          {alphabet.map((a) => (
            <button key={a} type="button" onClick={() => setLetter(letter === a ? null : a)} className={cn("rounded px-1.5 py-0.5 text-xs", letter === a ? "bg-prayer text-prayer-foreground" : "border border-border hover:bg-accent")}>{a}</button>
          ))}
        </div>
      )}

      {/* Semua — flat cross-type results */}
      {tab === "semua" && (
        <section className="mt-5">
          {semua.length === 0 ? (
            <p className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
              Tidak ada lokasi yang cocok. Coba kata kunci lain atau ganti filter.
            </p>
          ) : (
            <ul className="grid gap-1.5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {semua.map((l) => (
                <li key={`${l.kind}-${l.slug}`}>
                  <Link
                    to={locUrl(variant, l) as never}
                    className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm transition-colors hover:border-prayer hover:bg-prayer/5"
                  >
                    <Globe className="h-3.5 w-3.5 shrink-0 text-prayer" />
                    <span className="flex-1 truncate font-medium">{displayLocationTitle(l)}</span>
                    <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
                      {l.kind === "provinsi" ? "Prov" : l.kind === "kota" ? "Kota" : "Kec"}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {q.trim() === "" && (
            <p className="mt-3 text-xs text-muted-foreground">
              Menampilkan {Math.min(semua.length, 200)} pertama. Ketik di kolom pencarian untuk mempersempit.
            </p>
          )}
        </section>
      )}

      {/* Provinsi */}
      {tab === "provinsi" && (
        <section className="mt-5">
          <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {provinsi.map((p) => {
              const cKota = KOTA.filter((k) => k.parentSlug === p.slug).length;
              const cKec = KECAMATAN.filter((k) => k.provinceSlug === p.slug).length;
              return (
                <Link key={p.slug} to={locUrl(variant, p) as never} className="rounded-lg border border-border bg-card p-3 transition-colors hover:border-prayer hover:bg-prayer/5">
                  <div className="font-semibold text-foreground">{p.name}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{cKota} kota/kab · {cKec} kec</div>
                </Link>
              );
            })}
            {provinsi.length === 0 && <p className="col-span-full text-sm text-muted-foreground">Tidak ada provinsi yang cocok.</p>}
          </div>
        </section>
      )}

      {/* Kota */}
      {tab === "kota" && (
        <section className="mt-5 space-y-4">
          {PROVINSI.filter((p) => kotaByProv.get(p.slug)?.length).map((p) => (
            <div key={p.slug} className="rounded-lg border border-border bg-card p-4">
              <h3 className="mb-2 text-sm font-semibold text-muted-foreground">{p.name} ({kotaByProv.get(p.slug)!.length})</h3>
              <div className="flex flex-wrap gap-2">
                {kotaByProv.get(p.slug)!.map((k) => (
                  <Link key={k.slug} to={locUrl(variant, k) as never} className="rounded-md border border-border bg-background px-3 py-1 text-sm hover:border-prayer hover:text-prayer">{k.name}</Link>
                ))}
              </div>
            </div>
          ))}
          {kotaByProv.size === 0 && <p className="text-sm text-muted-foreground">Tidak ada kota yang cocok.</p>}
        </section>
      )}

      {/* Kecamatan */}
      {tab === "kecamatan" && (
        <section className="mt-5 space-y-4">
          {KOTA.filter((k) => kecByKota.get(k.slug)?.length).map((k) => (
            <div key={k.slug} className="rounded-lg border border-border bg-card p-4">
              <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                <MapPin className="h-3.5 w-3.5" /> {k.name} ({kecByKota.get(k.slug)!.length} kec)
              </h3>
              <div className="grid gap-1.5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {kecByKota.get(k.slug)!.map((kec) => (
                  <Link key={kec.slug} to={locUrl(variant, kec) as never} className="truncate rounded-md border border-border bg-background px-2.5 py-1 text-sm hover:border-prayer hover:text-prayer">{kec.name}</Link>
                ))}
              </div>
            </div>
          ))}
          {kecByKota.size === 0 && (
            <p className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
              Belum ada kecamatan yang cocok dengan filter ini. Dataset awal mencakup ±150 kecamatan populer; ekspansi bertahap ke 7.277 kecamatan se-Indonesia mengikuti data BPS.
            </p>
          )}
        </section>
      )}

      {tab === "favorit" && (
        <section className="mt-5 space-y-4">
          {favs.length === 0 ? (
            <p className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground">Belum ada lokasi favorit. Buka halaman jadwal lalu klik tombol ★ Favorit.</p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
              {favs.map((f) => (
                <Link key={f.href} to={f.href as never} className="flex items-center justify-between rounded-md border border-prayer/40 bg-prayer/5 px-3 py-2 text-sm text-prayer hover:bg-prayer/10">
                  <span>{f.name}</span>
                  <Star className="h-3.5 w-3.5 fill-current" />
                </Link>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Sources */}
      <section className="mt-8 rounded-lg border border-border bg-card p-5">
        <h2 className="mb-3 text-base font-semibold">Sumber Data</h2>
        <ul className="space-y-2 text-sm">
          {LOCATION_SOURCES.map((s) => (
            <li key={s.url}>
              <a href={s.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium text-prayer hover:underline">
                {s.name} <ExternalLink className="h-3 w-3" />
              </a>
              <p className="text-xs text-muted-foreground">{s.description}</p>
            </li>
          ))}
        </ul>
      </section>
    </SiteShell>
  );
}
